import React, { useId, useState } from 'react';
import { Report, ReportSummary } from '../../api/deviceListReports';
import Button from '../ui/Button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuLabel, DropdownMenuTrigger } from '../pilot-ui/dropdown-menu';

/**
 * FS-889 — the report trigger left of the search box and its menu: Default
 * view, the operator's own reports, the ones shared with them, then the
 * actions the open report's `can_*` flags allow. The menu closes before an
 * action runs; dialogs return focus to its live trigger when they close.
 */

export interface OpenReportView {
  id: number;
  /** The last saved copy; null while it is still loading or failed to load. */
  report: Report | null;
  loading: boolean;
  /** The view on screen is the report's (or the working view the report was
   *  restored under): there is something to save or copy. False while a
   *  report needed for the first render is loading or failed to load. */
  resolved: boolean;
}

export interface ReportMenuProps {
  open: OpenReportView | null;
  /** The view on screen differs from the saved report. */
  modified: boolean;
  /** FS-924: Save would store exactly the saved state, column widths included. */
  nothingToSave: boolean;
  /** FS-900: the server refused to read the open report; why, for the actions it would refuse too. */
  refusal: string | null;
  reports: ReportSummary[] | null;
  listError: string | null;
  /** Reports found to be saved by a newer console: listed, not openable. */
  unsupportedIds: ReadonlySet<number>;
  onRetryList: () => void;
  onDefaultView: () => void;
  onOpenReport: (id: number) => void;
  onSaveNew: () => void;
  onSave: () => void;
  onSaveCopy: () => void;
  onRename: () => void;
  onShare: () => void;
  onUnshare: () => void;
  onRevert: () => void;
  onDelete: () => void;
}

const itemClass = 'w-full text-left px-2 py-1 rounded-sm no-underline';
export const REPORT_MENU_TRIGGER_ID = 'device-list-report-menu-trigger';

const MenuItem: React.FC<{
  onClick: () => void;
  title: string;
  disabled?: boolean;
  disabledReason?: string;
  children: React.ReactNode;
  testId?: string;
}> = ({ onClick, title, disabled, disabledReason, children, testId }) => {
  const reasonId = useId();
  return (
    <>
      <DropdownMenuItem
        render={<Button variant="link" size="sm" title={title} disabled={disabled} />}
        nativeButton
        onClick={onClick}
        disabled={disabled}
        title={title}
        aria-describedby={disabledReason ? reasonId : undefined}
        className={itemClass}
        data-testid={testId}
      >
        {children}
      </DropdownMenuItem>
      {disabledReason && <p id={reasonId} className="px-2 py-1 text-xs text-muted-foreground">{disabledReason}</p>}
    </>
  );
};

const UNSUPPORTED = 'Saved by a newer version of the console';

const ReportMenu: React.FC<ReportMenuProps> = ({
  open,
  modified,
  nothingToSave,
  refusal,
  reports,
  listError,
  unsupportedIds,
  onRetryList,
  onDefaultView,
  onOpenReport,
  onSaveNew,
  onSave,
  onSaveCopy,
  onRename,
  onShare,
  onUnshare,
  onRevert,
  onDelete,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const pick = (fn: () => void) => () => {
    setMenuOpen(false);
    fn();
  };

  const report = open?.report ?? null;
  const name = report?.name ?? null;
  const label = open ? (name ?? (open.loading ? 'Loading report…' : 'Report')) : 'Unsaved view';
  const triggerTitle = open
    ? `Report "${name ?? open.id}"${modified ? ', changed since it was saved' : ''}. Open the menu to save, share, rename or switch reports.`
    : 'No report is open: the view on screen is not saved anywhere. Open the menu to save it as a report or open a saved one.';

  const mine = (reports ?? []).filter((r) => r.is_mine);
  const shared = (reports ?? []).filter((r) => !r.is_mine);

  const row = (r: ReportSummary) => {
    const unsupported = unsupportedIds.has(r.id);
    const current = open?.id === r.id;
    const owner = r.is_mine ? null : (r.owner_email ?? 'Deleted account');
    return (
      <MenuItem
        key={r.id}
        onClick={pick(() => onOpenReport(r.id))}
        disabled={unsupported}
        title={
          unsupported
            ? `"${r.name}": ${UNSUPPORTED.toLowerCase()}, so it cannot be opened here`
            : `Open "${r.name}"${r.shared_scope ? `, shared at ${r.shared_scope}` : ''}${owner ? ` by ${owner}` : ''}`
        }
        testId={`report-item-${r.id}`}
      >
        <span className={`block break-words ${current ? 'font-semibold' : ''}`} title={r.name}>
          {current ? '✓ ' : ''}
          {r.name}
        </span>
        {(r.shared_scope || owner || unsupported) && (
          <span className="block break-words text-xs font-normal text-muted-foreground">
            {[r.shared_scope, owner, unsupported ? UNSUPPORTED : null].filter(Boolean).join(' · ')}
          </span>
        )}
      </MenuItem>
    );
  };

  const can = (flag: keyof Pick<Report, 'can_edit' | 'can_share' | 'can_unshare' | 'can_delete'>) =>
    !!report && report[flag];
  /** The open report's own actions: off, with the reason, while the server refuses to read it, or
   *  (`noChange`) while the action would change nothing. */
  const ownAction = (title: string, noChange: string | null = null) => ({
    disabled: refusal !== null || noChange !== null,
    title: refusal ?? noChange ?? title,
  });

  return (
    <DropdownMenu
      open={menuOpen}
      onOpenChange={setMenuOpen}
    >
      <DropdownMenuTrigger
        render={<Button id={REPORT_MENU_TRIGGER_ID} variant="secondary" title={triggerTitle} className="max-w-xs" data-testid="report-menu-trigger" />}
      >
        <span className="truncate" title={name ?? undefined}>
          {open ? 'Report: ' : ''}
          {label}
        </span>
        {modified && (
          <span
            className="inline-flex items-center gap-1 text-xs text-amber-700"
            title="The view on screen differs from the saved report: columns, filters, sort, page size or search text changed. Save keeps it; Revert changes puts the saved version back."
            data-testid="report-modified"
          >
            <span aria-hidden className="inline-block h-2 w-2 rounded-full bg-status-warning-border" />
            Modified
          </span>
        )}
        <span aria-hidden className="shrink-0">▾</span>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" aria-label="Reports" className="w-80 max-h-96 overflow-y-auto p-2">
        <DropdownMenuGroup className="text-sm">
          <MenuItem
            onClick={pick(onDefaultView)}
            title="Show every column the console shows by default, with no filters, search or sort, and no report open"
            testId="report-default-view"
          >
            Default view
          </MenuItem>
        </DropdownMenuGroup>
        {listError ? (
          <DropdownMenuGroup>
            <p className="px-2 py-1 text-xs text-red-700" role="alert">Could not load your reports: {listError}</p>
            <MenuItem onClick={onRetryList} title="Load the report list again" testId="report-retry">Retry</MenuItem>
          </DropdownMenuGroup>
        ) : reports === null ? (
          <DropdownMenuGroup><p className="px-2 py-1 text-xs text-muted-foreground">Loading reports…</p></DropdownMenuGroup>
        ) : (
          <>
            <DropdownMenuGroup>
              <DropdownMenuLabel className="mt-2 px-2 font-semibold uppercase tracking-wide">My reports</DropdownMenuLabel>
              {mine.length === 0 ? (
                <p className="px-2 py-1 text-xs text-muted-foreground">None yet. Save report… keeps the view on screen.</p>
              ) : (
                mine.map(row)
              )}
            </DropdownMenuGroup>
            <DropdownMenuGroup>
              <DropdownMenuLabel className="mt-2 px-2 font-semibold uppercase tracking-wide">Shared with me</DropdownMenuLabel>
              {shared.length === 0 ? (
                <p className="px-2 py-1 text-xs text-muted-foreground">No one has shared a report at a path your scope covers.</p>
              ) : (
                shared.map(row)
              )}
            </DropdownMenuGroup>
          </>
        )}
        <DropdownMenuGroup className="mt-2 border-t pt-2 text-sm" data-testid="report-actions">
          {!open && (
            <MenuItem
              onClick={pick(onSaveNew)}
              title="Save the columns, filters, sort, page size and search text on screen as a new private report"
              testId="report-save-new"
            >
              Save report…
            </MenuItem>
          )}
          {open && (
            <>
              {can('can_edit') && (
                <MenuItem
                  onClick={pick(onSave)}
                  {...ownAction(
                    report?.shared_scope
                      ? 'Save the view on screen to this shared report, after showing what everyone at its path will see'
                      : 'Save the view on screen to this report',
                    nothingToSave ? 'No changes to save' : null,
                  )}
                  disabledReason={refusal ?? (nothingToSave ? 'No changes to save' : undefined)}
                  testId="report-save"
                >
                  Save
                </MenuItem>
              )}
              <MenuItem
                onClick={pick(onSaveCopy)}
                disabled={!open.resolved}
                title={
                  open.resolved
                    ? 'Save the view on screen as a new private report of your own, leaving this one as it is'
                    : 'The report is still loading; Save a copy is available once it has loaded'
                }
                testId="report-save-copy"
              >
                Save a copy…
              </MenuItem>
              {can('can_edit') && (
                <MenuItem onClick={pick(onRename)} {...ownAction('Give this report another name')} testId="report-rename">
                  Rename…
                </MenuItem>
              )}
              {can('can_share') && (
                <MenuItem
                  onClick={pick(onShare)}
                  {...ownAction(
                    report?.shared_scope
                      ? 'Share this report at a different path'
                      : 'Share this report with the accounts whose device scope covers a path you choose',
                  )}
                  testId="report-share"
                >
                  {report?.shared_scope ? 'Change sharing…' : 'Share…'}
                </MenuItem>
              )}
              {can('can_unshare') && (
                <MenuItem
                  onClick={pick(onUnshare)}
                  {...ownAction("Stop sharing this report; it goes back to its owner's private list")}
                  testId="report-unshare"
                >
                  Unshare…
                </MenuItem>
              )}
              {modified && (
                <MenuItem
                  onClick={pick(onRevert)}
                  title="Put the saved version of this report back, dropping the changes on screen"
                  testId="report-revert"
                >
                  Revert changes
                </MenuItem>
              )}
              {can('can_delete') && (
                <MenuItem
                  onClick={pick(onDelete)}
                  {...ownAction(
                    report?.shared_scope
                      ? 'Delete this report for everyone at its path; the view on screen stays'
                      : 'Delete this report; the view on screen stays',
                  )}
                  testId="report-delete"
                >
                  Delete…
                </MenuItem>
              )}
            </>
          )}
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default ReportMenu;
